import { AlertTriangle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const STATUS_STYLES = {
  Open: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  Assigned: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  "In Progress": "bg-indigo-500/15 text-indigo-300 border-indigo-500/40",
  Resolved: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  Closed: "bg-slate-500/15 text-slate-300 border-slate-500/40",
};

const PRIORITY_STYLES = {
  Critical: "bg-rose-500/20 text-rose-300 border-rose-500/40 font-bold",
  High: "bg-orange-500/20 text-orange-300 border-orange-500/40 font-semibold",
  Medium: "bg-amber-500/20 text-amber-300 border-amber-500/40",
  Low: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
};

export function StatusBadge({ status }) {
  return (
    <Badge data-testid={`status-badge-${status}`} variant="outline" className={`${STATUS_STYLES[status] || ""} px-2.5 py-0.5 rounded-full text-xs`}>
      {status}
    </Badge>
  );
}

export function PriorityBadge({ priority }) {
  return (
    <Badge data-testid={`priority-badge-${priority}`} variant="outline" className={`${PRIORITY_STYLES[priority] || ""} px-2.5 py-0.5 rounded-full text-xs`}>
      {priority}
    </Badge>
  );
}

export function LivePill() {
  return (
    <div data-testid="live-pill" className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass text-xs font-mono-alt uppercase tracking-wider text-emerald-300">
      <span className="live-dot" /> Live • Syncing 5s
    </div>
  );
}

// Left-strip color for complaint cards, driven by status.
// Open = red · Assigned = orange · In Progress = indigo · Resolved/Closed = green.
export function statusStripClass(status) {
  switch (status) {
    case "Open": return "!border-l-[6px] !border-l-rose-500 shadow-[inset_3px_0_10px_-6px_rgba(244,63,94,0.9)]";
    case "Assigned": return "!border-l-[6px] !border-l-orange-500 shadow-[inset_3px_0_10px_-6px_rgba(249,115,22,0.9)]";
    case "In Progress": return "!border-l-[6px] !border-l-indigo-400 shadow-[inset_3px_0_10px_-6px_rgba(129,140,248,0.9)]";
    case "Resolved":
    case "Closed": return "!border-l-[6px] !border-l-emerald-500 shadow-[inset_3px_0_10px_-6px_rgba(16,185,129,0.9)]";
    default: return "!border-l-[6px] !border-l-slate-700";
  }
}

// Overdue: any ticket still Open after N hours (default 4).
export function isOverdue(complaint, hours = 4) {
  if (!complaint || complaint.status !== "Open") return false;
  const createdMs = new Date(complaint.created_at).getTime();
  return (Date.now() - createdMs) > hours * 3600 * 1000;
}

export function OverdueBadge({ complaint, hours = 4 }) {
  if (!isOverdue(complaint, hours)) return null;
  return (
    <span data-testid="overdue-badge" className="pulse-danger inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-200 border border-rose-500/60 text-[10px] font-mono-alt uppercase tracking-wider">
      <AlertTriangle className="w-3 h-3" /> Overdue
    </span>
  );
}

export function OutageBadge({ complaint }) {
  if (!complaint?.outage_id || !complaint?.outage_device_name) return null;
  return (
    <span
      data-testid="outage-badge"
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-600/25 text-rose-100 border border-rose-500/70 text-[10px] font-mono-alt uppercase tracking-wider"
      title={`Linked to outage: ${complaint.outage_device_name}`}
    >
      <AlertTriangle className="w-3 h-3" /> {complaint.outage_device_type || "OLT"} · {complaint.outage_device_name}
    </span>
  );
}
